import React, { useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SplashCursor from '../components/SplashCursor';
import { FaHandshake, FaTools, FaGraduationCap, FaChartLine, FaCode, FaRocket, FaBrain } from 'react-icons/fa';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  position: relative;
  background: transparent;
  color: #fff;
  overflow: hidden;
  isolation: isolate;
  scroll-behavior: smooth;
  overflow-y: auto;

  /* Custom Scrollbar */
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.3);
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.8);
    border-radius: 5px;
    
    &:hover {
      background: rgba(255, 255, 255, 1);
    }
  }
`;

const ContentContainer = styled.div`
  position: relative;
  z-index: 50;
  padding: 0 5%;
  pointer-events: auto;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.8s ease-out;
  scroll-behavior: smooth;
`;

const HeroSection = styled.div`
  padding-top: 120px;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 8rem;
`;

const Title = styled.h1`
  font-size: 3.75rem;
  margin-bottom: 2rem;
  color: white;
  line-height: 1.1;
  font-weight: 500;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 3rem;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Section = styled.div`
  max-width: 1400px;
  margin: 0 auto 12rem;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  margin-bottom: 2rem;
  color: white;
  font-weight: 500;
  text-align: left;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  margin-top: 6rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: transparent;
  text-align: center;
  max-width: 400px;
  margin: 0 auto;
`;

const Icon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 2.5rem;
  color: #3B82F6;
  display: flex;
  justify-content: center;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  color: white;
  font-weight: 500;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 1.5rem;
`;

const CardDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 auto;
  max-width: 90%;
`;

const CTASection = styled.div`
  text-align: center;
  margin: 6rem auto;
  max-width: 800px;
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 1.2rem 3rem;
  background: white;
  color: #000;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 600;
  text-decoration: none;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(255, 255, 255, 0.3);
  }
`;

function PartnerProgram() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const partnerResources = [
    {
      icon: <FaCode />,
      title: "Developer Resources",
      description: "Access comprehensive documentation, sandbox environments, and reference applications to build and deploy solutions on Geo OS for Earth-scale operations."
    },
    {
      icon: <FaTools />,
      title: "Testing & Validation",
      description: "Utilize our testing frameworks and simulation environments to ensure your solutions meet the highest standards of reliability for mission-critical Earth operations."
    },
    {
      icon: <FaRocket />,
      title: "Integration Support",
      description: "Seamlessly integrate your solutions with Geo OS through our standardized APIs and SDKs, with direct access to our engineering team."
    }
  ];

  const benefits = [
    {
      icon: <FaBrain />,
      title: "Early Access",
      description: "Get priority access to new APIs, features, and Platform capabilities before general release, enabling you to build cutting-edge Earth operations solutions."
    },
    {
      icon: <FaTools />,
      title: "Technical Support",
      description: "Direct access to our engineering team for architecture reviews, technical guidance, and optimization of your integrated solutions."
    },
    {
      icon: <FaRocket />,
      title: "Innovation Partnership",
      description: "Collaborate on developing novel solutions and shape the future of autonomous Earth operations through direct input into our product roadmap."
    }
  ];

  return (
    <PageContainer>
      <SplashCursor />
      <Navbar />
      <ContentContainer>
        <HeroSection>
          <Title>
            Open Ecosystem <br /> for Autonomous Earth Operations
          </Title>
          <Subtitle>
            The Geo OS Partner Program enables organizations to create, integrate, and deploy
            transformative solutions for next-generation Earth operations.
          </Subtitle>
        </HeroSection>

        <Section>
          <SectionTitle>Partner Resources</SectionTitle>
          <Grid>
            {partnerResources.map((item, index) => (
              <Card key={`resource-${index}`}>
                <Icon>{item.icon}</Icon>
                <CardTitle>{item.title}</CardTitle>
                <CardDescription>{item.description}</CardDescription>
              </Card>
            ))}
          </Grid>
        </Section>

        <Section>
          <SectionTitle>Partner Benefits</SectionTitle>
          <Grid>
            {benefits.map((item, index) => (
              <Card key={`benefit-${index}`}>
                <Icon>{item.icon}</Icon>
                <CardTitle>{item.title}</CardTitle>
                <CardDescription>{item.description}</CardDescription>
              </Card>
            ))}
          </Grid>
        </Section>

        <CTASection>
          <Title style={{ fontSize: "2.5rem", marginBottom: "30px" }}>
            Ready to become a partner?
          </Title>
          <CTAButton href="/contact">
            Apply Now
          </CTAButton>
        </CTASection>

        <Footer />
      </ContentContainer>
    </PageContainer>
  );
}

export default PartnerProgram; 